/**
 * Scenario Comparison — run the engine across every scenario and show the
 * results side by side. This is where "scenario planning to determine
 * feasibility in advance" happens: same priorities and work, different
 * assumptions and capacity models per scenario.
 *
 * Read-only over engine output; no writes (build contract §4 — the engine
 * is a pre-POR workbench).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, GitCompareArrows, Loader2, Search, ShieldCheck, Zap } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import {
  runScenarioComparison,
  type ScenarioComparison,
} from '../../../lib/planningScenarioComparison';
import { resolveEngineScopeSet, engineInputLabel } from '../../../lib/planningEngineService';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import {
  computePorDeltas,
  isInPor,
  loadPorBaseline,
  type PorBaseline,
} from '../../../lib/porCompareBaseline';
import {
  FEASIBILITY_PLAIN_LANGUAGE,
  feasibilityTone,
  type FeasibilityTone,
} from '../../../lib/planningFeasibilityLanguage';
import { getItemCountState } from '../../../components/collection/types';
import { Select } from '../../../components/ds';
import { getLatestFinancialUtilization } from '../../../lib/timePhasedFinancialService';
import { formatDollars } from '../../../lib/timePhasedFinancialEngine';
import type { FundingAffordability } from '../../../lib/timePhasedFinancial.types';
import { affordabilitySentence, diffScenarioRuns } from '../../../lib/planningScenarioDiff';
import { ScenarioPairDiff } from './ScenarioPairDiff';
import { PLANNING_MODE_LABELS, type PriorityFeasibility } from '../../../lib/planningEngineModule.types';

const TONE_BADGE: Record<FeasibilityTone, string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]',
  amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]',
  red: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]',
  neutral: 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20',
};

function FeasibilityBadge({ feasibility, coverage }: { feasibility: PriorityFeasibility; coverage?: number }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE_BADGE[feasibilityTone(feasibility)]}`}>
      {feasibility}
      {coverage != null && feasibility !== 'No Work' ? ` · ${coverage}%` : ''}
    </span>
  );
}

export default function ScenarioComparisonView() {
  const { projectId, scenarios, activeScenario, engineInput, running: engineRunning } = usePlanningEngine();
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const [searchParams] = useSearchParams();
  const baselineId = searchParams.get('baseline');
  const hypothesisId = searchParams.get('hypothesis');
  const [comparison, setComparison] = useState<ScenarioComparison | null>(null);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [onlyDifferences, setOnlyDifferences] = useState(false);
  // The two scenarios read against each other (P-4). A launch what-if arrives
  // with its pair in the URL; otherwise A is the active scenario.
  const [pairA, setPairA] = useState<string | null>(baselineId);
  const [pairB, setPairB] = useState<string | null>(hypothesisId);
  // Last priced affordability per scenario id (P-6) — read from the snapshot
  // the Launch Calendar saves; null = never priced.
  const [affordability, setAffordability] = useState<Map<string, FundingAffordability | null>>(
    () => new Map(),
  );

  // POR baseline — derived from the active scenario's cycle binding, never picked.
  // Unbound scenario or POR-less cycle → no baseline, nothing blocks (Doc 102 §2A).
  const [porBaseline, setPorBaseline] = useState<PorBaseline | null>(null);
  const activeCycleId = activeScenario?.planCycleId ?? null;
  useEffect(() => {
    if (!activeCycleId) {
      setPorBaseline(null);
      return;
    }
    let cancelled = false;
    loadPorBaseline(activeCycleId)
      .then((b) => {
        if (!cancelled) setPorBaseline(b);
      })
      .catch(() => {
        if (!cancelled) setPorBaseline(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCycleId]);

  const porDeltas = useMemo(() => {
    if (!porBaseline || !comparison || !activeScenario) return null;
    const activeRun = comparison.results.find((r) => r.scenario.id === activeScenario.id);
    if (!activeRun?.output) return null;
    return computePorDeltas(porBaseline, activeRun.output.prioritySummaries);
  }, [porBaseline, comparison, activeScenario]);

  const baselineName = scenarios.find((s) => s.id === baselineId)?.name;
  const hypothesisName = scenarios.find((s) => s.id === hypothesisId)?.name;
  const fromLaunchWhatIf = Boolean(baselineId && hypothesisId);

  const run = async () => {
    if (!projectId || scenarios.length === 0) return;
    setComparing(true);
    setError(null);
    try {
      await Promise.all([fetchItems(), fetchRelationships()]);
      const items = useItemsStore.getState().items;
      const edges = useItemRelationshipsStore.getState().edges;

      // Re-resolve fresh, same discipline as the single-scenario run path
      // (PlanningEngineContext.runActiveInstance) — never trust a stale Set.
      let scopeSet: Set<string> | null = null;
      if (engineInput.type !== 'live') {
        scopeSet = await resolveEngineScopeSet(engineInput);
        if (!scopeSet || scopeSet.size === 0) {
          setError(
            `Input scope "${engineInputLabel(engineInput)}" resolved to 0 items. ` +
              'Check that the queue/pool/collection has members, then try again.',
          );
          return;
        }
      }

      const [result, priced] = await Promise.all([
        runScenarioComparison(projectId, scenarios, items, edges, scopeSet, engineInput.type),
        Promise.all(
          scenarios.map((sc) =>
            getLatestFinancialUtilization(sc.id)
              .then((f) => [sc.id, f.affordability] as const)
              .catch(() => [sc.id, null] as const),
          ),
        ),
      ]);
      setAffordability(new Map(priced));
      setComparison(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  useEffect(() => {
    if (!fromLaunchWhatIf || !projectId || scenarios.length === 0 || comparison || comparing) {
      return;
    }
    void run();
    // Auto-run once when arriving from Launch Calendar change set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLaunchWhatIf, projectId, scenarios.length]);

  const pair = useMemo(() => {
    if (!comparison || comparison.results.length < 2) return null;
    const ids = comparison.results.map((r) => r.scenario.id);
    const a = pairA && ids.includes(pairA) ? pairA : (activeScenario?.id && ids.includes(activeScenario.id) ? activeScenario.id : ids[0]);
    const b = pairB && ids.includes(pairB) && pairB !== a ? pairB : ids.find((id) => id !== a)!;
    const runA = comparison.results.find((r) => r.scenario.id === a)!;
    const runB = comparison.results.find((r) => r.scenario.id === b)!;
    return { a, b, runA, runB, diff: diffScenarioRuns(runA, runB) };
  }, [comparison, pairA, pairB, activeScenario?.id]);

  // priority id → (scenario id → summary)
  const matrix = useMemo(() => {
    if (!comparison) return new Map<string, Map<string, { feasibility: PriorityFeasibility; coverage: number }>>();
    const m = new Map<string, Map<string, { feasibility: PriorityFeasibility; coverage: number }>>();
    for (const r of comparison.results) {
      for (const ps of r.output?.prioritySummaries ?? []) {
        const row = m.get(ps.priority.id) ?? new Map();
        row.set(r.scenario.id, { feasibility: ps.feasibility, coverage: ps.coveragePercent });
        m.set(ps.priority.id, row);
      }
    }
    return m;
  }, [comparison]);

  const visiblePriorityIds = useMemo(() => {
    if (!comparison) return [];
    const q = query.trim().toLowerCase();
    return comparison.priorityIds.filter((id) => {
      const name = comparison.priorityNames.get(id) ?? '';
      if (q && !name.toLowerCase().includes(q)) return false;
      if (onlyDifferences) {
        const row = matrix.get(id);
        if (!row) return false;
        const values = [...row.values()].map((v) => v.feasibility);
        if (new Set(values).size <= 1 && values.length === comparison.results.length) return false;
      }
      return true;
    });
  }, [comparison, query, onlyDifferences, matrix]);

  return (
    <div className="p-4 md:p-6 w-full space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20">
              <GitCompareArrows className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Scenario Comparison
            </h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] ml-10">
            Run every scenario against{' '}
            <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
              {engineInputLabel(engineInput)}
            </span>{' '}
            and see which become achievable under each set of assumptions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void run()}
          disabled={comparing || engineRunning || scenarios.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
        >
          {comparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          Run all {scenarios.length} scenario{scenarios.length === 1 ? '' : 's'}
        </button>
      </div>

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {fromLaunchWhatIf && (
        <div className="border border-primary-200 dark:border-primary-800/40 bg-primary-50/60 dark:bg-primary-900/15 px-4 py-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Comparing Launch Calendar what-if:{' '}
          <strong>{baselineName ?? 'Baseline'}</strong>
          <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]"> vs </span>
          <strong>{hypothesisName ?? 'Hypothesis'}</strong>
          , read against each other under Compare two once the run finishes.{' '}
          <Link to="/planning/launch-calendar" className="text-primary-600 dark:text-primary-400 hover:underline">
            Back to Launch Calendar
          </Link>
        </div>
      )}

      {scenarios.length === 0 && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-12 text-center">
          <GitCompareArrows className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No scenarios yet.{' '}
            <Link to="/planning/engine/scenarios" className="text-primary-600 hover:underline">
              Create at least two scenarios
            </Link>{' '}
            to compare planning alternatives.
          </p>
        </div>
      )}

      {scenarios.length === 1 && !comparison && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] px-4 py-3 text-sm text-[var(--text-sec)]">
          Only one scenario exists. Duplicate it on the{' '}
          <Link to="/planning/engine/scenarios" className="underline font-medium">Scenarios tab</Link>{' '}
          and change one assumption (horizon, buffer, capacity) to make comparison meaningful.
        </div>
      )}

      {!comparison && scenarios.length > 0 && (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-12 text-center">
          <GitCompareArrows className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No comparison run yet. Click <strong>Run all scenarios</strong> to see them side by side.
          </p>
        </div>
      )}

      {porBaseline && (
        <div className="border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)]/60 dark:bg-[var(--slate)]/20 px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            <ShieldCheck className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] shrink-0" />
            <span>
              <strong>Plan of Record baseline</strong> — {porBaseline.cycleName}, authorized{' '}
              {new Date(porBaseline.authorizedAt).toLocaleDateString()} ·{' '}
              {porBaseline.initiatives.length} authorized initiative{porBaseline.initiatives.length === 1 ? '' : 's'}.
              Read-only reference; derived from the active scenario’s cycle binding.
            </span>
          </div>
          {porDeltas && (
            <div className="mt-2 ml-6 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {porDeltas.atRisk.length === 0 && porDeltas.notInRun.length === 0 ? (
                <span className="text-[var(--green)] dark:text-[var(--text-ter)]">
                  ✓ Every POR initiative holds under “{activeScenario?.name}”.
                </span>
              ) : (
                <>
                  {porDeltas.atRisk.length > 0 && (
                    <span className="text-[var(--coral)] dark:text-[var(--coral)]" title={porDeltas.atRisk.map((i) => i.title).join(', ')}>
                      {porDeltas.atRisk.length} POR initiative{porDeltas.atRisk.length === 1 ? '' : 's'} not achievable under “{activeScenario?.name}”:{' '}
                      {porDeltas.atRisk.slice(0, 4).map((i) => i.title).join(', ')}
                      {porDeltas.atRisk.length > 4 ? ` +${porDeltas.atRisk.length - 4} more` : ''}
                    </span>
                  )}
                  {porDeltas.notInRun.length > 0 && (
                    <span className="text-[var(--mustard)] dark:text-[var(--mustard)]" title={porDeltas.notInRun.map((i) => i.title).join(', ')}>
                      {porDeltas.notInRun.length} not in this run:{' '}
                      {porDeltas.notInRun.slice(0, 4).map((i) => i.title).join(', ')}
                      {porDeltas.notInRun.length > 4 ? ` +${porDeltas.notInRun.length - 4} more` : ''}
                    </span>
                  )}
                </>
              )}
              {porDeltas.beyondPor.length > 0 && (
                <span className="text-[var(--text-ter)] dark:text-[var(--text-sec)]" title={porDeltas.beyondPor.map((p) => p.name).join(', ')}>
                  {porDeltas.beyondPor.length} achievable beyond the POR
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {comparison && (
        <>
          {/* Scenario summary columns */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(comparison.results.length, 4)}, minmax(0, 1fr))` }}>
            {comparison.results.map(({ scenario, output, error: runError }) => {
              const exec = output?.executiveSummary ?? null;
              return (
                <div
                  key={scenario.id}
                  className={`bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]  border p-4 ${
                    scenario.active ? 'border-primary-300 dark:border-primary-700 ring-1 ring-primary-100 dark:ring-primary-900/40' : 'border-[var(--rule)] dark:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{scenario.name}</p>
                    {scenario.active && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400 shrink-0">Active</span>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                    {PLANNING_MODE_LABELS[scenario.planningMode]} · {scenario.planningWeeks} wk · {Math.round(scenario.capacityBufferPercent * 100)}% buffer
                  </p>
                  {exec ? (
                    <div className="mt-3 space-y-1.5">
                      <FeasibilityBadge feasibility={exec.overallFeasibility} />
                      <div className="grid grid-cols-3 gap-1 text-center mt-2">
                        <div>
                          <div className="text-lg font-bold text-[var(--green)] dark:text-[var(--text-ter)] tabular-nums">{exec.includedCount}</div>
                          <div className="text-[10px] text-[var(--text-ter)] uppercase tracking-wide">In</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-[var(--mustard)] dark:text-[var(--mustard)] tabular-nums">{exec.deferredCount}</div>
                          <div className="text-[10px] text-[var(--text-ter)] uppercase tracking-wide">Out - capacity</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-[var(--coral)] dark:text-[var(--coral)] tabular-nums">
                            {exec.blockedCount + exec.notReadyCount + exec.noCapacityModelCount}
                          </div>
                          <div className="text-[10px] text-[var(--text-ter)] uppercase tracking-wide">Out</div>
                        </div>
                      </div>
                      <p
                        className={`text-xs ${
                          affordability.get(scenario.id)?.withinEnvelope === false
                            ? 'text-[var(--coral)]'
                            : 'text-[var(--text-ter)] dark:text-[var(--text-sec)]'
                        }`}
                        data-testid="scenario-affordability"
                      >
                        {affordabilitySentence(affordability.get(scenario.id) ?? null, formatDollars)}
                      </p>
                      {(exec.participationAtRiskCount > 0 || exec.participationBlockedCount > 0) && (
                        <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                          {exec.participationAtRiskCount + exec.participationBlockedCount} participation risk{exec.participationAtRiskCount + exec.participationBlockedCount === 1 ? '' : 's'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] mt-3">{runError}</p>
                  )}
                </div>
              );
            })}
          </div>

          {pair && (
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 space-y-4" data-testid="scenario-pair">
              <div className="flex items-center gap-2 flex-wrap text-sm text-[var(--text-pri)]">
                <span className="font-semibold uppercase tracking-wider text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mr-1">
                  Compare two
                </span>
                <div className="w-60">
                  <Select
                    size="sm"
                    aria-label="Scenario A"
                    value={pair.a}
                    onChange={(e) => setPairA(e.target.value)}
                    options={comparison.results.map((r) => ({ value: r.scenario.id, label: r.scenario.name }))}
                  />
                </div>
                <span className="text-[var(--text-ter)]">to</span>
                <div className="w-60">
                  <Select
                    size="sm"
                    aria-label="Scenario B"
                    value={pair.b}
                    onChange={(e) => setPairB(e.target.value)}
                    options={comparison.results
                      .filter((r) => r.scenario.id !== pair.a)
                      .map((r) => ({ value: r.scenario.id, label: r.scenario.name }))}
                  />
                </div>
              </div>
              <ScenarioPairDiff
                nameA={pair.runA.scenario.name}
                nameB={pair.runB.scenario.name}
                diff={pair.diff}
                affordabilityA={affordabilitySentence(affordability.get(pair.a) ?? null, formatDollars)}
                affordabilityB={affordabilitySentence(affordability.get(pair.b) ?? null, formatDollars)}
              />
            </div>
          )}

          {/* Per-priority feasibility matrix */}
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-[var(--rule)] dark:border-[var(--rule)]">
              <h3 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                Priority feasibility by scenario
              </h3>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={onlyDifferences}
                    onChange={(e) => setOnlyDifferences(e.target.checked)}
                    className="rounded border-[var(--rule)]"
                  />
                  Only differences
                </label>
                {getItemCountState(comparison.priorityIds.length) === 'many' && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <input
                      type="search"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search priorities…"
                      className="pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 w-52"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                <thead className="bg-[var(--surface)] dark:bg-[var(--bg)]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                      Priority
                    </th>
                    {porBaseline && (
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wider"
                        title={`Authorized Plan of Record for ${porBaseline.cycleName} — read-only baseline`}
                      >
                        PoR
                      </th>
                    )}
                    {comparison.results.map(({ scenario }) => (
                      <th key={scenario.id} className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                        {scenario.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                  {visiblePriorityIds.length === 0 && (
                    <tr>
                      <td colSpan={comparison.results.length + (porBaseline ? 2 : 1)} className="px-4 py-8 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                        {onlyDifferences ? 'No priorities differ between scenarios.' : 'No priorities match.'}
                      </td>
                    </tr>
                  )}
                  {visiblePriorityIds.map((pid) => {
                    const row = matrix.get(pid);
                    const values = row ? [...row.values()].map((v) => v.feasibility) : [];
                    const differs = new Set(values).size > 1 || (row?.size ?? 0) < comparison.results.length;
                    return (
                      <tr key={pid} className={differs ? 'bg-[var(--slate-dim)]/40 dark:bg-[var(--slate)]/10' : undefined}>
                        <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] break-words min-w-[280px]">
                          {comparison.priorityNames.get(pid)}
                        </td>
                        {porBaseline && (
                          <td className="px-4 py-3">
                            {isInPor(porBaseline, pid, comparison.priorityNames.get(pid) ?? '') ? (
                              <CheckCircle2 className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" aria-label="In Plan of Record" />
                            ) : (
                              <span className="text-xs text-[var(--text-ter)]" aria-label="Not in Plan of Record">—</span>
                            )}
                          </td>
                        )}
                        {comparison.results.map(({ scenario }) => {
                          const cell = row?.get(scenario.id);
                          return (
                            <td key={scenario.id} className="px-4 py-3">
                              {cell ? (
                                <FeasibilityBadge feasibility={cell.feasibility} coverage={cell.coverage} />
                              ) : (
                                <span className="text-xs text-[var(--text-ter)]">Not in run</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Plain-language legend: {(['Fully Achievable', 'Partially Achievable', 'Not Achievable'] as const)
              .map((f) => `${f} = ${FEASIBILITY_PLAIN_LANGUAGE[f]}`)
              .join(' · ')}
          </p>
        </>
      )}
    </div>
  );
}
